import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { useUiStore } from '@/stores/ui-store';

import { ApiError, type ApiErrorDetails } from '@/lib/api/api-error';
import { passUrl } from '@/lib/api/pass-origin';
import { recoverFrom401 } from '@/lib/api/session-guard';

/**
 * Client de l'API v2 du Pass, surface de l'espace perso (`/api/v2/perso`).
 *
 * Même socle que l'espace pro : enveloppe `{ success, data, message, meta }` déballée par les
 * fonctions ci-dessous, erreurs normalisées en `ApiError`. Les cookies HttpOnly du Pass portent
 * l'authentification, d'où `withCredentials`.
 *
 * Statuts :
 * - **401** : session finie → rafraîchissement Gravitee, puis éjection (cf. `session-guard`) ;
 * - **403** : action non permise → l'erreur remonte à l'écran, **sans** déconnexion ;
 * - **503 `MICROSERVICE_UNAVAILABLE`** : un service est tombé (CMS, Calendar…) → l'écran concerné
 *   l'affiche, le reste du site continue. Même traitement pour un 503 `INTERNAL_ERROR` : c'est la
 *   forme que prend une erreur de route v2 déléguée à la v1 (`DelegatesToV1`), Calendar en panne
 *   par exemple. Seul un 503 sans enveloppe d'erreur — le Pass entier hors service — bascule
 *   l'application en maintenance, comme la v1.
 */

export interface ApiEnvelope<T> {
    success: boolean;
    data: T;
    message?: string;
    meta?: Record<string, unknown>;
}

export interface ApiMeta {
    current_page: number | null;
    per_page: number | null;
    total: number | null;
    last_page: number | null;
}

const MESSAGE_GENERIQUE = 'Service momentanément indisponible';

const create = (path: string): AxiosInstance =>
    axios.create({
        baseURL: passUrl(path),
        withCredentials: true,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    });

/** Surface authentifiée de l'assuré. */
export const apiV2 = create('/api/v2/perso');
/** Référentiels publics v2 (pays, communes, marques blanches). */
export const apiV2Public = create('/api/v2');

interface ErreurPass {
    code: string;
    message: string;
    id?: string;
    details?: ApiErrorDetails;
}

/**
 * Erreur du Pass, telle qu'elle arrive dans le corps de la réponse : enveloppe v2
 * `{ success: false, error: { code, id, message, details } }`, ou corps plat `{ message }` des
 * middlewares d'authentification.
 */
function lireErreur(body: unknown, status: number): ErreurPass {
    if (typeof body !== 'object' || body === null) {
        return { code: 'API_ERROR', message: MESSAGE_GENERIQUE };
    }

    const corps = body as Record<string, unknown>;
    const imbriquee = corps['error'];

    if (typeof imbriquee === 'object' && imbriquee !== null) {
        const erreur = imbriquee as Record<string, unknown>;

        return {
            code: typeof erreur['code'] === 'string' ? erreur['code'] : 'API_ERROR',
            message: typeof erreur['message'] === 'string' ? erreur['message'] : MESSAGE_GENERIQUE,
            id: typeof erreur['id'] === 'string' ? erreur['id'] : undefined,
            details:
                typeof erreur['details'] === 'object' && erreur['details'] !== null
                    ? (erreur['details'] as ApiErrorDetails)
                    : undefined,
        };
    }

    return {
        code: typeof imbriquee === 'string' ? imbriquee : status === 401 ? 'UNAUTHENTICATED' : 'API_ERROR',
        message: typeof corps['message'] === 'string' ? corps['message'] : MESSAGE_GENERIQUE,
    };
}

/** Traduit toute erreur réseau en `ApiError`. */
export function toApiError(error: unknown): ApiError {
    if (error instanceof ApiError) return error;

    if (!axios.isAxiosError(error)) {
        return new ApiError('UNKNOWN', 'Une erreur inattendue est survenue', 0);
    }

    if (!error.response) {
        return new ApiError('NETWORK_ERROR', 'Service injoignable', 0);
    }

    const status = error.response.status;
    const { code, message, id, details } = lireErreur(error.response.data, status);

    /*
     * Un 5xx `INTERNAL_ERROR` recopie l'exception brute quand `APP_DEBUG` est actif : on la tait.
     * En dessous de 500, `INTERNAL_ERROR` est la forme des refus délégués à la v1
     * (`abort(400, message)` d'ABS) : leur message est écrit pour l'assuré, on le garde.
     */
    if (status >= 500 && (code === 'INTERNAL_ERROR' || code === 'API_ERROR')) {
        return new ApiError(code === 'API_ERROR' ? 'SERVER_ERROR' : code, MESSAGE_GENERIQUE, status, id);
    }

    return new ApiError(code, message, status, id, details);
}

for (const instance of [apiV2, apiV2Public]) {
    instance.interceptors.response.use(
        (response) => response,
        (error: unknown) => {
            const apiError = toApiError(error);

            // Une requête déjà rejouée après rafraîchissement ne repart pas dans un second cycle.
            const config = axios.isAxiosError(error) ? (error.config as (typeof error.config & { _retried?: boolean }) | undefined) : undefined;
            if (apiError.isUnauthenticated && config && !config._retried) {
                return recoverFrom401(() => instance.request({ ...config, _retried: true } as AxiosRequestConfig), apiError);
            }

            if (apiError.httpStatus === 503 && !apiError.isMicroserviceUnavailable && apiError.code !== 'INTERNAL_ERROR') {
                useUiStore.getState().setMaintenance(true);
            }

            return Promise.reject(apiError);
        },
    );
}

/** Déballe l'enveloppe v2 : les hooks manipulent la donnée, pas le contenant. */
export async function getData<T>(url: string, params?: Record<string, unknown>, client = apiV2): Promise<T> {
    const { data } = await client.get<ApiEnvelope<T>>(url, { params });
    return data.data;
}

/** Variante paginée : la liste et sa `meta`. */
export async function getPaginated<T>(
    url: string,
    params?: Record<string, unknown>,
): Promise<{ data: T; meta: ApiMeta }> {
    const { data } = await apiV2.get<ApiEnvelope<T>>(url, { params });
    const meta = (data.meta ?? {}) as Partial<ApiMeta>;

    return {
        data: data.data,
        meta: {
            current_page: meta.current_page ?? null,
            per_page: meta.per_page ?? null,
            total: meta.total ?? null,
            last_page: meta.last_page ?? null,
        },
    };
}

/** Écriture : renvoie l'enveloppe complète, dont `message` sert souvent de retour à l'assuré. */
export async function postEnvelope<T>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig,
    client = apiV2,
): Promise<ApiEnvelope<T>> {
    const { data } = await client.post<ApiEnvelope<T>>(url, body, config);
    return data;
}

export async function postData<T>(url: string, body?: unknown, config?: AxiosRequestConfig, client = apiV2): Promise<T> {
    return (await postEnvelope<T>(url, body, config, client)).data;
}

export async function putData<T>(url: string, body?: unknown, config?: AxiosRequestConfig, client = apiV2): Promise<T> {
    const { data } = await client.put<ApiEnvelope<T>>(url, body, config);
    return data.data;
}

/**
 * Envoi multipart. `Content-Type` retiré pour qu'axios écrive lui-même la limite (`boundary`)
 * du `FormData`.
 */
export async function postFormData<T>(url: string, form: FormData, client = apiV2): Promise<ApiEnvelope<T>> {
    const { data } = await client.post<ApiEnvelope<T>>(url, form, { headers: { 'Content-Type': undefined } });
    return data;
}

export async function deleteData<T>(url: string): Promise<T> {
    const { data } = await apiV2.delete<ApiEnvelope<T>>(url);
    return data.data;
}
